import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { history } from 'nallian-shared-ui/lib/config/history';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { Flex, Loader } from 'nallian-shared-ui/lib/components';
import appSubscriptionsService from '../../services/appSubscriptions/appSubscriptionsService';
import availableAppSubscriptionsService from '../../services/appSubscriptions/availableAppSubscriptionsService';
import createAppSubscriptionService from '../../services/appSubscriptions/createAppSubscriptionService';
import updateAppSubscriptionService from '../../services/appSubscriptions/updateAppSubscriptionService';
import removeAppSubscriptionService from '../../services/appSubscriptions/removeAppSubscriptionService';
import AppSubscription from './AppSubscription';
import AppSubscriptionFeatureForm from './AppSubscriptionFeatureForm';
import AppSubscriptionForm from './AppSubscriptionForm';
import { APP_SUBSCRIPTION_ACTIONS } from '../../constants';
import { mapAppSubscriptions } from '../../helpers/appSubscriptions/mapAppSubscriptions';

const ORGANISATION_APP_RIGHTS = [
  'ORGANISATIONAPPS.MANAGE.ALL',
  'ORGANISATIONAPPS.MANAGE.ALL.EXCEPTSYSTEMORGS',
  'ORGANISATIONAPPS.MANAGE.ORGANISATION',
];

const AppSubscriptions = ({
  dispatchAppSubscriptionsGet,
  appSubscriptionsFetch,
  dispatchAvailableAppSubscriptionsGet,
  availableAppSubscriptionsFetch,
  dispatchCreateAppSubscriptionPost,
  dispatchUpdateAppSubscriptionPut,
  dispatchRemoveAppSubscriptionDelete,
  createAppSubscriptionFetch,
  updateAppSubscriptionFetch,
  organisationId,
  urlRoot,
  forbidManageBecauseSystemOrg,
}) => {
  const { t } = useTranslation();
  const { action, actionId } = useParams();
  const { search } = useLocation();

  const params = new URLSearchParams(search);
  const communityAppId = params.get('communityAppId');

  const appsubscriptions = appSubscriptionsFetch.value || [];
  const availableCommunityApps = availableAppSubscriptionsFetch.value || [];

  useEffect(() => {
    dispatchAppSubscriptionsGet(organisationId, t);
  }, [dispatchAppSubscriptionsGet, organisationId, t]);

  useEffect(() => {
    dispatchAvailableAppSubscriptionsGet(organisationId, t);
  }, [dispatchAvailableAppSubscriptionsGet, organisationId, t]);

  useEffect(() => {
    if (availableAppSubscriptionsFetch.value?.length > 0)
      history.push(`${urlRoot}/app-subscriptions/new`);
  }, [urlRoot, availableAppSubscriptionsFetch.value]);

  const handleRefetch = useCallback(() => {
    dispatchAppSubscriptionsGet(organisationId, t);
    dispatchAvailableAppSubscriptionsGet(organisationId, t);
    history.push(`${urlRoot}/app-subscriptions`);
  }, [
    organisationId,
    urlRoot,
    dispatchAppSubscriptionsGet,
    dispatchAvailableAppSubscriptionsGet,
    t,
  ]);

  const handleSubmit = useCallback(
    body => {
      const existingApp = appsubscriptions.find(c => c.id === communityAppId);
      if (existingApp) {
        dispatchUpdateAppSubscriptionPut(
          mapAppSubscriptions(organisationId, communityAppId, existingApp, body),
          handleRefetch,
          t
        );
      } else {
        dispatchCreateAppSubscriptionPost(
          mapAppSubscriptions(organisationId, communityAppId, undefined, body),
          handleRefetch,
          t
        );
      }
    },
    [
      appsubscriptions,
      communityAppId,
      organisationId,
      dispatchCreateAppSubscriptionPost,
      dispatchUpdateAppSubscriptionPut,
      handleRefetch,
      t,
    ]
  );

  const handleRemoveFeature = useCallback(
    (_, { organisationApp, feature }) => {
      if ((organisationApp?.communityAppFeatures || []).length > 1) {
        dispatchUpdateAppSubscriptionPut(
          mapAppSubscriptions(
            organisationId,
            organisationApp.id,
            organisationApp,
            undefined,
            feature
          ),
          handleRefetch,
          t
        );
      } else {
        dispatchRemoveAppSubscriptionDelete(
          {
            organisation: { id: organisationId },
            communityApp: { id: organisationApp.id },
          },
          handleRefetch,
          t
        );
      }
    },
    [
      organisationId,
      handleRefetch,
      dispatchRemoveAppSubscriptionDelete,
      dispatchUpdateAppSubscriptionPut,
      t,
    ]
  );

  const selectedFeature = useMemo(() => {
    const communityApp = appsubscriptions.find(ca => ca.id === communityAppId);
    return (communityApp?.communityAppFeatures || []).find(
      f => f.appInstallationFeature?.tag === actionId
    );
  }, [communityAppId, appsubscriptions, actionId]);

  if (appSubscriptionsFetch.pending || availableAppSubscriptionsFetch.pending) {
    return (
      <Flex fluid justifyContent="center">
        <Loader active />
      </Flex>
    );
  }

  return (
    <Flex fluid direction="column">
      {!forbidManageBecauseSystemOrg && (
        <HasSomeRights rights={ORGANISATION_APP_RIGHTS}>
          <AppSubscriptionFeatureForm
            open={
              action === APP_SUBSCRIPTION_ACTIONS.NEW_FEATURE ||
              action === APP_SUBSCRIPTION_ACTIONS.EDIT_FEATURE
            }
            editMode={action === APP_SUBSCRIPTION_ACTIONS.EDIT_FEATURE}
            data={selectedFeature}
            communityAppId={communityAppId}
            appsubscriptions={appsubscriptions}
            availableCommunityApps={availableCommunityApps}
            submitProcessing={
              createAppSubscriptionFetch.pending || updateAppSubscriptionFetch.pending
            }
            onSubmit={handleSubmit}
          />
        </HasSomeRights>
      )}
      <Flex fluid direction="column">
        {!forbidManageBecauseSystemOrg && (
          <HasSomeRights rights={ORGANISATION_APP_RIGHTS}>
            {action === APP_SUBSCRIPTION_ACTIONS.NEW && (
              <AppSubscriptionForm
                urlRoot={urlRoot}
                isNewAppSubscription
                communityAppId={communityAppId}
                appsubscriptions={appsubscriptions}
                availableCommunityApps={availableCommunityApps}
              />
            )}
          </HasSomeRights>
        )}
        {appsubscriptions.map((organisationApp, i) => (
          <AppSubscription
            urlRoot={urlRoot}
            isLast={i >= appsubscriptions.length - 1}
            organisationApp={organisationApp}
            key={organisationApp.id}
            availableCommunityApps={availableCommunityApps}
            handleRemoveFeature={handleRemoveFeature}
            forbidManageBecauseSystemOrg={forbidManageBecauseSystemOrg}
          />
        ))}
      </Flex>
    </Flex>
  );
};

AppSubscriptions.propTypes = {
  organisationId: PropTypes.string.isRequired,
  urlRoot: PropTypes.string.isRequired,
  dispatchAppSubscriptionsGet: PropTypes.func.isRequired,
  appSubscriptionsFetch: PropTypes.object.isRequired,
  dispatchAvailableAppSubscriptionsGet: PropTypes.func.isRequired,
  availableAppSubscriptionsFetch: PropTypes.object.isRequired,
  createAppSubscriptionFetch: PropTypes.object.isRequired,
  dispatchCreateAppSubscriptionPost: PropTypes.func.isRequired,
  updateAppSubscriptionFetch: PropTypes.object.isRequired,
  dispatchUpdateAppSubscriptionPut: PropTypes.func.isRequired,
  dispatchRemoveAppSubscriptionDelete: PropTypes.func.isRequired,
  forbidManageBecauseSystemOrg: PropTypes.bool.isRequired,
};

export default compose(
  appSubscriptionsService,
  availableAppSubscriptionsService,
  createAppSubscriptionService,
  updateAppSubscriptionService,
  removeAppSubscriptionService
)(AppSubscriptions);
